import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Share, Link as LinkIcon, Copy, Eye, Trash, ShieldSlash } from "@phosphor-icons/react";

/**
 * Admin-only panel to generate / share / revoke a public client link
 * pointing to /public/chantier/{token}.
 */
export default function ShareLinkPanel({ chantier, onChanged }) {
  const [token, setToken] = useState(chantier?.share_token || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setToken(chantier?.share_token || null); }, [chantier?.share_token]);

  const publicUrl = token ? `${window.location.origin}/public/chantier/${token}` : "";

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/chantiers/${chantier.id}/share-link`);
      setToken(r.data.token);
      toast.success("Lien client généré");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Révoquer le lien public ? Le client n'aura plus accès.")) return;
    setBusy(true);
    try {
      await api.delete(`/chantiers/${chantier.id}/share-link`);
      setToken(null);
      toast.success("Lien révoqué");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Lien copié");
    } catch { toast.error("Copie impossible"); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2" data-testid="share-link-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5"><Share size={14} weight="duotone" className="text-[#005CFF]" /> Portail client</div>
          <div className="text-[10px] text-white/50 mt-0.5">Un lien public à envoyer au client — il verra le suivi photos, debriefs et rapport signé.</div>
        </div>
        {token && (
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-[10px] text-[#7CB2FF] hover:underline flex items-center gap-1" data-testid="share-preview-btn">
            <Eye size={11} /> Aperçu
          </a>
        )}
      </div>

      {token ? (
        <>
          <div className="flex items-center gap-1.5">
            <div className="flex-1 min-w-0 flex items-center gap-1.5 rounded-md bg-black/40 border border-white/10 px-2 py-2">
              <LinkIcon size={11} className="text-white/40 shrink-0" />
              <span className="text-[11px] font-mono text-white/70 truncate" data-testid="share-url">{publicUrl}</span>
            </div>
            <Button onClick={copy} variant="ghost" className="border border-white/10 h-9 px-2.5" data-testid="share-copy-btn">
              <Copy size={12} />
            </Button>
          </div>
          <Button onClick={revoke} disabled={busy} variant="ghost" className="w-full text-red-300 hover:text-red-200 hover:bg-red-500/10 h-8 text-xs" data-testid="share-revoke-btn">
            <ShieldSlash size={12} className="mr-1" /> Révoquer le lien
          </Button>
        </>
      ) : (
        <Button onClick={generate} disabled={busy} className="w-full bg-[#005CFF] hover:bg-[#0047CC] h-9" data-testid="share-generate-btn">
          {busy ? "…" : (<><Share size={13} className="mr-1.5" weight="bold" /> Partager avec le client</>)}
        </Button>
      )}
    </div>
  );
}
